import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePrompts } from '../context/PromptContext';
import PromptCard from './PromptCard';
import SearchBar from './SearchBar';
import BulkActions from './BulkActions';
import PromptDetailsModal from './PromptDetailsModal';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiGrid, FiList, FiDatabase } = FiIcons;

const Dashboard = () => {
  const { prompts, selectedPrompts } = usePrompts();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  // Default to 'list' view on mobile (< 768px), 'grid' on desktop
  const [viewMode, setViewMode] = useState(() => 
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'grid'
  );
  const [viewingPrompt, setViewingPrompt] = useState(null);

  const filteredAndSortedPrompts = useMemo(() => {
    let filtered = prompts.filter(prompt =>
      prompt.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'created_at' || sortBy === 'updated_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [prompts, searchTerm, sortBy, sortOrder]);

   return (
      <div className="max-w-7xl mx-auto pb-12 px-4 sm:px-0 pb-bottom-nav relative overscroll-contain">
        <div className="flex flex-col lg:flex-row gap-4 mb-8 items-start lg:items-center">
         <div className="flex-1 w-full lg:w-auto">
           <SearchBar
             searchTerm={searchTerm}
             onSearchChange={setSearchTerm}
             sortBy={sortBy}
             onSortByChange={setSortBy}
             sortOrder={sortOrder}
             onSortOrderChange={setSortOrder}
           />
         </div>

         <div className="flex gap-4 w-full lg:w-auto">
              <Link
                to="/editor/new"
                className="neo-btn neo-btn-primary flex items-center justify-center h-12 min-w-[44px] whitespace-nowrap flex-1 lg:flex-none"
              >
               <SafeIcon icon={FiPlus} className="mr-2" size={20} />
               <span className="hidden md:inline">Create New</span>
             </Link>

             <div className="flex items-center space-x-3 bg-white border-2 border-black p-2 shadow-neo h-12">
               <span className="font-bold text-sm mr-2 hidden sm:inline select-none">View:</span>
               <button
                 onClick={() => setViewMode('grid')}
                 className={`p-2 min-w-[44px] min-h-[44px] border-2 border-black transition-all cursor-pointer flex items-center justify-center ${
                   viewMode === 'grid'
                     ? 'bg-black text-white shadow-none'
                     : 'bg-white text-black hover:bg-gray-100'
                 }`}
                 title="Grid View"
               >
                 <SafeIcon icon={FiGrid} size={18} />
               </button>
               <button
                 onClick={() => setViewMode('list')}
                 className={`p-2 min-w-[44px] min-h-[44px] border-2 border-black transition-all cursor-pointer flex items-center justify-center ${
                   viewMode === 'list'
                     ? 'bg-black text-white shadow-none'
                     : 'bg-white text-black hover:bg-gray-100'
                 }`}
                 title="List View"
               >
                 <SafeIcon icon={FiList} size={18} />
               </button>
             </div>
        </div>
      </div>

      {/* Mobile FAB */}
       <Link
         to="/editor/new"
         className="md:hidden fixed bottom-[88px] right-4 z-40 bg-neo-blue border-2 border-black shadow-neo p-4 rounded-full hover:scale-105 transition-transform active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
         aria-label="Create New Prompt"
       >
        <SafeIcon icon={FiPlus} size={24} />
      </Link>

      {selectedPrompts.length > 0 && (
        <div className="mb-8">
          <BulkActions />
        </div>
      )}

      {filteredAndSortedPrompts.length === 0 ? (
        <div className="neo-card bg-white p-12 text-center border-2 border-black shadow-neo-lg">
          <div className="inline-block p-6 bg-neo-purple border-2 border-black rounded-full mb-6 shadow-neo">
            <SafeIcon icon={FiDatabase} size={48} className="text-black" />
          </div>
          <h3 className="text-2xl font-bold text-black mb-2 uppercase">No Data Found</h3>
          <p className="text-gray-600 mb-8 font-mono">
            {prompts.length === 0
              ? 'Library is empty. Initiate first entry.'
              : 'Search query returned 0 results.'}
          </p>
          {prompts.length === 0 && (
             <Link
               to="/editor/new"
               className="neo-btn neo-btn-primary inline-flex items-center"
             >
              <SafeIcon icon={FiPlus} className="mr-2" size={16} />
              Initialize Template
            </Link>
          )}
        </div>
      ) : (
        <div className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8'
            : 'space-y-4 sm:space-y-6'
        }>
          {filteredAndSortedPrompts.map(prompt => (
            <PromptCard
              key={prompt.id}
              prompt={prompt}
              viewMode={viewMode}
              onView={(p) => setViewingPrompt(p)}
            />
          ))}
        </div>
      )}

      {/* Prompt Details Modal */}
      {viewingPrompt && (
        <PromptDetailsModal
          prompt={viewingPrompt}
          onClose={() => setViewingPrompt(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
